import { useCallback } from 'react';
import * as Cesium from 'cesium';
import type {
  GroundStation,
  GeoFeature,
  AccessWindowPair,
} from '../../../types/mission';
import type { SatelliteGroupDataType } from '../../../../../modelling/types';
import {
  LOS_GS_SATELLITE_COLOR,
  LOS_TARGET_SATELLITE_COLOR,
  LOS_SATELLITE_SATELLITE_COLOR,
} from '../constants';

export function useLineOfSight(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  secondaryViewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  sharedDataSourceRef: React.MutableRefObject<any>,
  entityGroupMap: React.MutableRefObject<Map<string, string[]>>,
  layersRef: React.MutableRefObject<any>,
  losVisibilityHoldRef: React.MutableRefObject<Map<string, number>>,
  satOrbitActiveMap: React.MutableRefObject<Record<string, boolean>>,
  clearGroup: (group: string) => void,
) {
  function isLOSClear(p1: Cesium.Cartesian3, p2: Cesium.Cartesian3): boolean {
    const diff = Cesium.Cartesian3.subtract(p2, p1, new Cesium.Cartesian3());
    const len = Cesium.Cartesian3.magnitude(diff);
    if (len < 1) return true;
    const dir = Cesium.Cartesian3.divideByScalar(diff, len, new Cesium.Cartesian3());
    const ray = new Cesium.Ray(p1, dir);
    const interval = Cesium.IntersectionTests.rayEllipsoid(ray, Cesium.Ellipsoid.WGS84);
    if (!interval) return true;
    return interval.start < 0 || interval.start > len;
  }

  function elevationAngle(satPos: Cesium.Cartesian3, gsPos: Cesium.Cartesian3): number {
    const toSat = Cesium.Cartesian3.subtract(satPos, gsPos, new Cesium.Cartesian3());
    const up = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(gsPos, new Cesium.Cartesian3());
    const cosA = Cesium.Cartesian3.dot(
      Cesium.Cartesian3.normalize(toSat, new Cesium.Cartesian3()),
      up,
    );
    return Math.asin(Math.max(-1, Math.min(1, cosA)));
  }

  const drawLOS = useCallback((
    groups: SatelliteGroupDataType[],
    gsList: GroundStation[],
    focusedSatId?: string | null,
    accessWindowPairs: AccessWindowPair[] = [],
    geoList: GeoFeature[] = [],
  ) => {
    const v = viewerRef.current;
    const ds = sharedDataSourceRef.current;
    if (!v || !ds) return;
    clearGroup('los');
    if (!layersRef.current.los) return;

    const satIds: string[] = [];
    groups.forEach((g) =>
      g.satellites.forEach((s) => {
        if ((s.orbitParameters?.semiMajorAxis ?? 0) > 0) satIds.push(s.id);
      }),
    );

    const ids: string[] = [];
    const hasAccessWindows = accessWindowPairs.length > 0;
    const isPairActive = (
      time: Cesium.JulianDate,
      satelliteId: string,
      counterpartyId: string,
      counterpartyKind: AccessWindowPair['counterpartyKind'],
      requireWindow = false,
    ) => {
      if (!hasAccessWindows) return !requireWindow;
      const epoch = Cesium.JulianDate.toDate(time).getTime() / 1000;
      return accessWindowPairs.some(pair => {
        if (pair.status !== 'ok') return false;
        if (pair.counterpartyKind !== counterpartyKind) return false;
        const direct = pair.satelliteId === satelliteId && pair.counterpartyId === counterpartyId;
        const reverseSatSat = counterpartyKind === 'satellite' && pair.satelliteId === counterpartyId && pair.counterpartyId === satelliteId;
        if (!direct && !reverseSatSat) return false;
        return pair.passes?.some(pass => epoch >= pass.start && epoch <= pass.end);
      });
    };

    for (let i = 0; i < satIds.length; i++) {
      for (let j = i + 1; j < satIds.length; j++) {
        const idA = satIds[i];
        const idB = satIds[j];
        if (focusedSatId && idA !== focusedSatId && idB !== focusedSatId) continue;
        const linkId = `los_ss_${idA}_${idB}`;

        ds.entities.add({
          id: linkId,
          polyline: {
            arcType: Cesium.ArcType.NONE,
            positions: new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
              const eA = sharedDataSourceRef.current?.entities.getById(idA);
              const eB = sharedDataSourceRef.current?.entities.getById(idB);
              if (!eA || !eB) return [];
              const pA = eA.position?.getValue(time);
              const pB = eB.position?.getValue(time);
              if (!pA || !pB) return [];
              return [pA, pB];
            }, false),
            show: new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
              if (!layersRef.current.los) return false;
              if (layersRef.current.satellites === false) return false;
              if (satOrbitActiveMap.current[idA] === false || satOrbitActiveMap.current[idB] === false) return false;
              if (!isPairActive(time, idA, idB, 'satellite')) return false;
              const eA = sharedDataSourceRef.current?.entities.getById(idA);
              const eB = sharedDataSourceRef.current?.entities.getById(idB);
              if (!eA || !eB) return false;
              const pA = eA.position?.getValue(time);
              const pB = eB.position?.getValue(time);
              if (!pA || !pB) return false;
              return isLOSClear(pA, pB);
            }, false),
            width: 0.8,
            material: new Cesium.ColorMaterialProperty(LOS_SATELLITE_SATELLITE_COLOR.withAlpha(0.78)),
          },
        });
        ids.push(linkId);
      }
    }

    satIds.filter(id => !focusedSatId || id === focusedSatId).forEach((satId) => {
      gsList.forEach((gs) => {
        if (gs.active === false) return;
        const gsPos = Cesium.Cartesian3.fromDegrees(gs.lon, gs.lat, (gs.altKm ?? 0) * 1000);
        const minElevRad = Cesium.Math.toRadians(Math.max(gs.minElevation ?? 5, 0));
        const linkId = `los_sg_${satId}_${gs.id}`;
        const isGroundStationLinkVisible = (time: Cesium.JulianDate) => {
          if (!layersRef.current.los) return false;
          if (layersRef.current.satellites === false) return false;
          if (satOrbitActiveMap.current[satId] === false) return false;
          const holdUntil = losVisibilityHoldRef.current.get(linkId) ?? 0;
          const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
          const releaseHold = () => {
            if (holdUntil > now) return true;
            if (holdUntil) losVisibilityHoldRef.current.delete(linkId);
            return false;
          };
          if (!isPairActive(time, satId, gs.id, 'ground_station')) return releaseHold();
          const eS = sharedDataSourceRef.current?.entities.getById(satId);
          if (!eS) return releaseHold();
          const pS = eS.position?.getValue(time);
          if (!pS) return releaseHold();
          const visible = isLOSClear(pS, gsPos) && elevationAngle(pS, gsPos) >= minElevRad;
          if (visible) {
            losVisibilityHoldRef.current.set(linkId, now + 1800);
            return true;
          }
          return releaseHold();
        };

        ds.entities.add({
          id: linkId,
          polyline: {
            arcType: Cesium.ArcType.NONE,
            positions: new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
              const eS = sharedDataSourceRef.current?.entities.getById(satId);
              if (!eS) return [];
              const pS = eS.position?.getValue(time);
              if (!pS) return [];
              return [pS, gsPos];
            }, false),
            show: new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
              return isGroundStationLinkVisible(time);
            }, false),
            width: 1.5,
            material: new Cesium.ColorMaterialProperty(LOS_GS_SATELLITE_COLOR.withAlpha(0.76)),
          },
        });
        ids.push(linkId);

        const glowLinkId = `los_sg_glow_${satId}_${gs.id}`;
        ds.entities.add({
          id: glowLinkId,
          polyline: {
            arcType: Cesium.ArcType.NONE,
            positions: new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
              const eS = sharedDataSourceRef.current?.entities.getById(satId);
              if (!eS) return [];
              const pS = eS.position?.getValue(time);
              if (!pS) return [];
              return [pS, gsPos];
            }, false),
            show: new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
              return isGroundStationLinkVisible(time);
            }, false),
            width: 5,
            material: new Cesium.PolylineGlowMaterialProperty({
              color: LOS_GS_SATELLITE_COLOR.withAlpha(0.46),
              glowPower: 0.16,
              taperPower: 1.0,
            }),
          },
        });
        ids.push(glowLinkId);
      });
    });

    const getGeoPoint = (geo: GeoFeature): Cesium.Cartesian3 | null => {
      if (geo.kind === 'point' && geo.lng != null && geo.lat != null) {
        return Cesium.Cartesian3.fromDegrees(geo.lng, geo.lat, (geo.altitude ?? 0) * 1000);
      }
      if (geo.kind === 'circle' && geo.centerLng != null && geo.centerLat != null) {
        return Cesium.Cartesian3.fromDegrees(geo.centerLng, geo.centerLat, 0);
      }
      if (geo.coordinates?.length) {
        const sum = geo.coordinates.reduce((acc, point) => ({
          lat: acc.lat + point.lat,
          lng: acc.lng + point.lng,
        }), { lat: 0, lng: 0 });
        return Cesium.Cartesian3.fromDegrees(sum.lng / geo.coordinates.length, sum.lat / geo.coordinates.length, 0);
      }
      return null;
    };

    satIds.filter(id => !focusedSatId || id === focusedSatId).forEach((satId) => {
      geoList.forEach((geo) => {
        const targetPos = getGeoPoint(geo);
        if (!targetPos) return;
        const kind: AccessWindowPair['counterpartyKind'] = geo.kind === 'point' ? 'geo_feature_spot' : 'geo_feature_geofence';
        const linkId = `los_st_${satId}_${geo.id}`;

        ds.entities.add({
          id: linkId,
          polyline: {
            arcType: Cesium.ArcType.NONE,
            positions: new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
              const eS = sharedDataSourceRef.current?.entities.getById(satId);
              if (!eS) return [];
              const pS = eS.position?.getValue(time);
              if (!pS) return [];
              return [pS, targetPos];
            }, false),
            show: new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
              if (geo.kind !== 'point') return false;
              if (!layersRef.current.los) return false;
              if (layersRef.current.satellites === false) return false;
              if (satOrbitActiveMap.current[satId] === false) return false;
              if (!isPairActive(time, satId, geo.id, kind, true)) return false;
              const eS = sharedDataSourceRef.current?.entities.getById(satId);
              if (!eS) return false;
              const pS = eS.position?.getValue(time);
              return !!pS;
            }, false),
            width: 1.35,
            material: new Cesium.ColorMaterialProperty(LOS_TARGET_SATELLITE_COLOR.withAlpha(0.72)),
          },
        });
        ids.push(linkId);
      });
    });

    entityGroupMap.current.set('los', ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearGroup]);

  const clearLOS = useCallback(() => {
    clearGroup('los');
  }, [clearGroup]);

  return {
    drawLOS,
    clearLOS,
  };
}
